import { type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { CoreBox, useTheme } from '@vitvio/design-system';
import { type Nullable } from '@vitvio/helpers';
import { Cards } from './cards';
import { ORColumnHeader } from './ORColumnHeader';
import { useORColumn } from './hooks/useORColumn';
import { MIN_SURGERY_CARD_DURATION_MINUTES, MIN_TURNOVER_CARD_DURATION_MINUTES } from '../constants';

interface Props {
  id: string;
  headerPortalRef: RefObject<Nullable<HTMLDivElement>>;
  headerPortalReady: boolean;
}

export function ORColumn({ id, headerPortalRef, headerPortalReady }: Props) {
  const { getLayout } = useTheme();
  const minuteHeight = getLayout('timeline', 'minuteHeight', false);
  const cardMargin = getLayout('timeline', 'cardMargin', false); // TODO: add these values to spacing values, should be pb="layout.timeline.cardMargin"

  const { operatingRoomName, orStatus, slots, isLoading } = useORColumn({
    id,
  });

  const canPortal = headerPortalReady && !!headerPortalRef.current;

  if (isLoading) {
    return null;
  }

  const headerContent = <ORColumnHeader name={operatingRoomName} status={orStatus} />;

  return (
    <>
      {/* Render header via portal into the fixed header bar */}
      {canPortal &&
        createPortal(
          <CoreBox flexGrow={1} ml="3">
            {headerContent}
          </CoreBox>,
          headerPortalRef.current!,
        )}

      <CoreBox flexGrow={1} relative fullH>
        {slots.map((slot) => {
          const Card = Cards[slot.type];
          const minDuration =
            slot.type === 'surgery'
              ? MIN_SURGERY_CARD_DURATION_MINUTES
              : slot.type === 'turnover'
                ? MIN_TURNOVER_CARD_DURATION_MINUTES
                : 0;
          const clampedEnd = Math.min(slot.endPosition, 24 * 60);
          const duration = Math.max(clampedEnd - slot.startPosition, minDuration);
          const top = slot.startPosition * minuteHeight;
          const height = duration * minuteHeight;
          return (
            <CoreBox px="1" key={slot.id} absolute left={0} right={0} top={top} h={height} pb={cardMargin}>
              <Card {...slot} />
            </CoreBox>
          );
        })}
      </CoreBox>
    </>
  );
}
